import { useState, useEffect } from "react";
import { Search, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useProfiles } from "@/hooks/useProfiles";
import { supabase } from "@/integrations/supabase/client";
import { PlatformIcons } from "@/components/profiles/PlatformIcons";

interface TagProfilesTabProps {
  tagId: string;
}

export const TagProfilesTab = ({ tagId }: TagProfilesTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [assignedProfileIds, setAssignedProfileIds] = useState<string[]>([]);
  const [isFetching, setIsFetching] = useState(true);
  const { profiles, loading } = useProfiles();

  useEffect(() => {
    const fetchAssignedProfiles = async () => {
      try {
        setIsFetching(true);
        const { data, error } = await supabase
          .from('profile_tags')
          .select('profile_id')
          .eq('tag_id', tagId);
        
        if (error) {
          console.error('Error fetching profile tags:', error);
          setAssignedProfileIds([]);
        } else {
          setAssignedProfileIds(data?.map(item => item.profile_id) || []);
        }
      } catch (err) {
        console.error('Error:', err);
        setAssignedProfileIds([]);
      } finally {
        setIsFetching(false);
      }
    };
    
    fetchAssignedProfiles();
  }, [tagId]);

  // Filter to only show profiles that are in the database assignment
  const taggedProfiles = profiles.filter(profile => 
    assignedProfileIds.includes(profile.id)
  );

  const filteredProfiles = taggedProfiles.filter(profile =>
    profile.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    profile.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isLoading = loading || isFetching;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search profiles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {filteredProfiles.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <CardTitle className="text-center mb-2">No Profiles Found</CardTitle>
            <CardDescription className="text-center">
              {searchQuery 
                ? "No profiles match your search criteria."
                : "This tag has no configuration profiles assigned to it yet."
              }
            </CardDescription>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProfiles.map((profile) => (
            <Card key={profile.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-base">
                      {profile.name}
                    </CardTitle>
                    {profile.description && (
                      <CardDescription className="text-sm line-clamp-2">
                        {profile.description}
                      </CardDescription>
                    )}
                  </div>
                  {profile.isDraft && (
                    <Badge variant="secondary">
                      Draft
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payloads:</span>
                    <span>{profile.payloads.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Platforms:</span>
                    <PlatformIcons platforms={profile.platforms} size="sm" />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created:</span>
                    <span>{new Date(profile.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};